/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\UserProfilesSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Users module.
----------------------------------------------------------------*/


import { Building, Shield, Upload, User, X } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import Toggle from '../../../components/ui/Toggle';
import { ExtensionSelect } from '../../../components/ui/ExtensionSelect';

interface UserProfilesSectionProps {
    formData: {
        profileId: string;
        logProfileId: string;
        totpEnabled: boolean;
        debugMode: boolean;
        extensionId: number | string | null;
        isActive: boolean;
        isPrimaryAdmin: boolean;
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    avatar: string | null;
    uploading: boolean;
    debugModeActive: boolean;
    onInputChange: (field: string, value: unknown) => void;
    onAvatarSelect: () => void;
    onAvatarRemove: () => void;
    t: (key: string) => string;
}

function FieldLabel({ label, debugKey, debugModeActive }: { label: string; debugKey: string; debugModeActive: boolean }) {
    return (
        <div className="flex flex-col">
            <label className="text-sm font-medium text-foreground">{label}</label>
            {debugModeActive && <span className="text-xs text-muted-foreground mt-0.5">{debugKey}</span>}
        </div>
    );
}

export function UserProfilesSection({ formData, avatar, uploading, debugModeActive, onInputChange, onAvatarSelect, onAvatarRemove, t }: UserProfilesSectionProps) {
    return (
        <FormBox title={t('users.sections.profilesConfiguration')} icon={<Building className="h-5 w-5" />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                {/* TOTP Enabled */}
                <SimpleTooltip content={t('users.tooltips.totpEnabled')}>
                    <FieldLabel label={t('users.totpEnabled')} debugKey="totp_enabled" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex justify-start">
                    <Toggle value={formData.totpEnabled} onChange={(value) => onInputChange('totpEnabled', value)} />
                </div>

                {/* Debug Mode */}
                <SimpleTooltip content={t('users.tooltips.debugMode')}>
                    <FieldLabel label={t('users.debugMode')} debugKey="debug_mode" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex justify-start">
                    <Toggle value={formData.debugMode} onChange={(value) => onInputChange('debugMode', value)} />
                </div>

                {/* Extension */}
                <SimpleTooltip content={t('users.tooltips.extension')}>
                    <FieldLabel label={t('users.extension')} debugKey="extension_id" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <ExtensionSelect
                    value={formData.extensionId?.toString() || ''}
                    onChange={(value) => onInputChange('extensionId', value || null)}
                    placeholder={t('users.selectExtension')}
                    valProperty="id"
                />

                {/* Enabled */}
                <SimpleTooltip content={formData.isPrimaryAdmin ? t('users.tooltips.cannotDeactivatePrimary') : t('users.tooltips.active')}>
                    <FieldLabel label={t('users.enabled')} debugKey="is_active" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex justify-start">
                    <Toggle
                        value={formData.isActive}
                        onChange={(value) => onInputChange('isActive', value)}
                        disabled={formData.isPrimaryAdmin}
                    />
                </div>

                {/* Avatar */}
                <SimpleTooltip content={t('users.tooltips.avatar')}>
                    <FieldLabel label={t('users.avatar')} debugKey="avatar" debugModeActive={debugModeActive} />
                </SimpleTooltip>
                <div className="flex items-center gap-3">
                    <div className="relative w-12 h-12 border-2 border-border rounded-lg bg-muted flex items-center justify-center">
                        {avatar ? (
                            <>
                                <img src={avatar} alt="Avatar" className="w-full h-full object-cover rounded-lg" />
                                {!uploading && (
                                    <button
                                        type="button"
                                        onClick={onAvatarRemove}
                                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                        title="Remove avatar"
                                    >
                                        <X className="w-2.5 h-2.5 text-white" />
                                    </button>
                                )}
                                {uploading && (
                                    <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    </div>
                                )}
                            </>
                        ) : (
                            <User className="w-6 h-6 text-muted-foreground" />
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onAvatarSelect}
                        disabled={uploading}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/90 hover:bg-primary/10 rounded-md transition-colors border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Upload className="w-3 h-3 text-primary" />
                        {uploading ? t('users.uploading') : avatar ? t('users.changeImage') : t('users.selectImage')}
                    </button>
                </div>
            </div>
        </FormBox>
    );
}
